import { useState } from "react";
import { X, CalendarPlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import ClientDemoBookingModal from "./ClientDemoBookingModal";
import { DISPO } from "./ClientCallDispositionModal";
import EhiOutcomeHistory from "./EhiOutcomeHistory";

const NOT_INTERESTED = ["No current requirement", "Already using another solution", "Cost / budget", "Declined to share reason", "Other"];
const DATED = ["Retry Call", "Book Product Demo", "Schedule Callback", "Follow Up Later"];
const EMPTY = { contact_status: "", not_connected_reason: "", contact_person: "", products_pitched: [], interest_level: "",
  not_interested_reason: "", next_action: "", followup_date: "", followup_time: "", info_products: [], trial_product: "", notes: "" };
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const C = {
  input: { width: "100%", border: "1px solid #cfd6e2", borderRadius: 9, padding: "10px 12px", background: "#fff", color: "#172033", fontFamily: "inherit", fontSize: 13, outline: "none" },
  label: { display: "block", fontSize: 12.5, fontWeight: 700, marginBottom: 7, color: "#344054" },
  hint: { color: "#667085", fontSize: 11.5, marginTop: 6, lineHeight: 1.4 },
  err: { color: "#d92d20", fontSize: 11.5, marginTop: 5, fontWeight: 600 },
  grid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 15 },
};

/** Returns {field: message} for every missing required field. */
function validate(d) {
  const e = {};
  const connected = d.contact_status === "Connected";
  if (!d.contact_status) e.contact_status = "Select a contact status";
  if (d.contact_status === "Not Connected" && !d.not_connected_reason) e.not_connected_reason = "Select a reason";
  if (connected) {
    if (!d.contact_person) e.contact_person = "Select the contact person";
    if (!d.products_pitched.length) e.products_pitched = "Select at least one product";
    if (!d.interest_level) e.interest_level = "Select the client's interest";
  }
  if (!d.next_action) e.next_action = "Select a next action";
  if (DATED.includes(d.next_action) && !d.followup_date) e.followup_date = "Select a follow-up date";
  if (d.next_action === "Send Product Information" && !d.info_products.length) e.info_products = "Select at least one product";
  if (d.next_action === "Start Free Trial" && !d.trial_product) e.trial_product = "Select the trial product";
  return e;
}

function Section({ n, title, req, children, testid, last }) {
  return (
    <section data-testid={testid} style={{ borderBottom: last ? "none" : "1px solid #edf0f5", paddingBottom: last ? 0 : 18, marginBottom: last ? 0 : 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span style={{ width: 28, height: 28, display: "grid", placeItems: "center", borderRadius: 8, background: "#eef4ff", color: "#175cd3", fontWeight: 700, fontSize: 13 }}>{n}</span>
        <h2 style={{ fontSize: 15, margin: 0, color: "#172033" }}>{title}{req && <span style={{ color: "#d92d20" }}> *</span>}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, req, error, hint, children, full }) {
  return (
    <div style={{ minWidth: 0, gridColumn: full ? "1 / -1" : undefined }}>
      <label style={C.label}>{label}{req && <span style={{ color: "#d92d20" }}> *</span>}</label>
      {children}
      {error ? <div style={C.err}>{error}</div> : hint ? <div style={C.hint}>{hint}</div> : null}
    </div>
  );
}

function Select({ value, onChange, placeholder, options, testid, error }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} style={{ ...C.input, borderColor: error ? "#f97066" : "#cfd6e2" }}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function ProductTiles({ selected, onToggle, prefix, single }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
      {DISPO.products.map((p) => {
        const on = selected.includes(p);
        return (
          <label key={p} data-testid={`${prefix}-${slug(p)}`} style={{ display: "flex", gap: 9, alignItems: "flex-start", border: `1px solid ${on ? "#528bff" : "#dfe4ec"}`, background: on ? "#f5f8ff" : "#fff", borderRadius: 10, padding: 11, fontSize: 13, lineHeight: 1.35, cursor: "pointer", transition: "background-color 120ms, border-color 120ms" }}>
            <input type={single ? "radio" : "checkbox"} checked={on} onChange={() => onToggle(p)} style={{ margin: "2px 0 0", accentColor: "#175cd3" }} />
            {p}
          </label>
        );
      })}
    </div>
  );
}

/** EHI Client Call Disposition — built from ehi_client_call_disposition.html. */
export default function EhiLogOutcomeModal({ client, clientName, clientId, saving, onSave, onClose, initialNotes }) {
  const [demoOpen, setDemoOpen] = useState(false);
  const [booked, setBooked] = useState(null);
  const [d, setD] = useState({ ...EMPTY, notes: initialNotes || "" });
  const [errors, setErrors] = useState({});
  const set = (patch) => { setD((x) => ({ ...x, ...patch })); setErrors((e) => { const n = { ...e }; Object.keys(patch).forEach((k) => delete n[k]); return n; }); };
  const connected = d.contact_status === "Connected";
  const submit = () => {
    const e = validate(d);
    setErrors(e);
    if (Object.keys(e).length) return;
    const out = connected ? d : { ...d, contact_person: "", products_pitched: [], interest_level: "", not_interested_reason: "" };
    onSave(d.next_action === "Book Product Demo" && booked?.id ? { ...out, demo_booking_id: booked.id } : out);
  };
  const errCount = Object.keys(errors).length;
  return (
    <div data-testid="ehi-log-outcome-modal" style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(11,17,32,0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 24px 84px", fontFamily: "Inter, Arial, sans-serif" }}>
      {demoOpen && (
        <ClientDemoBookingModal client={client} products={d.products_pitched} date={d.followup_date} time={d.followup_time}
          onClose={() => setDemoOpen(false)}
          onBooked={(b) => {
            setDemoOpen(false); setBooked(b);
            if (b?.date) set({ followup_date: b.date, followup_time: b.time || d.followup_time });
            toast.success("Client demo booked — now save the outcome");
          }} />
      )}
      <div style={{ width: 1280, maxWidth: "100%", maxHeight: "calc(100vh - 108px)", background: "#f4f6fa", borderRadius: 16, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 60px -16px rgba(0,0,0,0.3)" }}>
        <header style={{ padding: "20px 22px 14px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, margin: "0 0 6px", color: "#172033" }}>EHI Client Call Disposition</h1>
            <div style={{ color: "#667085", fontSize: 13, lineHeight: 1.45 }}>Capture contact outcome, products pitched, interest, and next action.</div>
            <div data-testid="ehi-log-outcome-client" style={{ fontSize: 12.5, color: "#344054", fontWeight: 600, marginTop: 6 }}>{clientName} <span style={{ color: "#98a2b3", fontWeight: 500 }}>#{clientId}</span></div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ background: "#e8f1ff", color: "#175cd3", borderRadius: 999, padding: "7px 11px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap" }}>EHI CLIENT · LOG CALL</span>
            <button data-testid="ehi-log-outcome-close" onClick={onClose} title="Close" style={{ border: "none", background: "none", cursor: "pointer", color: "#667085" }}><X style={{ width: 18, height: 18 }} /></button>
          </div>
        </header>
        <div style={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "minmax(0, 1fr) 400px", gap: 16, padding: "0 22px" }}>
        <div style={{ overflowY: "auto", minHeight: 0 }}>
          <div style={{ background: "#fff", border: "1px solid #e1e6ef", borderRadius: 16, padding: 22, boxShadow: "0 8px 24px rgba(16,24,40,.05)" }}>
            <Section n={1} title="Contact Outcome" testid="lo-section-contact">
              <div style={C.grid}>
                <Field label="Contact Status" req error={errors.contact_status}>
                  <Select testid="lo-contact-status" value={d.contact_status} onChange={(v) => set({ contact_status: v })} placeholder="Select contact status" options={["Connected", "Not Connected"]} error={errors.contact_status} />
                </Field>
                {d.contact_status === "Not Connected" && <Field label="Not Connected Reason" req error={errors.not_connected_reason}>
                  <Select testid="lo-not-connected-reason" value={d.not_connected_reason} onChange={(v) => set({ not_connected_reason: v })} placeholder="Select reason" options={DISPO.notReasons} error={errors.not_connected_reason} />
                </Field>}
                {connected && <Field label="Contact Person" req error={errors.contact_person}>
                  <Select testid="lo-contact-person" value={d.contact_person} onChange={(v) => set({ contact_person: v })} placeholder="Select contact person" options={DISPO.persons} error={errors.contact_person} />
                </Field>}
              </div>
            </Section>
            {connected && <Section n={2} title="Products Pitched" req testid="lo-section-products">
              <ProductTiles prefix="lo-product" selected={d.products_pitched} onToggle={(p) => set({ products_pitched: toggle(d.products_pitched, p) })} />
              {errors.products_pitched ? <div style={C.err}>{errors.products_pitched}</div> : <div style={C.hint}>Select every product discussed during this call.</div>}
            </Section>}
            {connected && <Section n={3} title="Interest Level" testid="lo-section-interest">
              <div style={C.grid}>
                <Field label="Client Interest" req error={errors.interest_level}>
                  <Select testid="lo-interest" value={d.interest_level} onChange={(v) => set({ interest_level: v, not_interested_reason: "" })} placeholder="Select interest level" options={DISPO.interest} error={errors.interest_level} />
                </Field>
                {d.interest_level === "Not Interested" && <Field label="Reason Not Interested">
                  <Select testid="lo-not-interested-reason" value={d.not_interested_reason} onChange={(v) => set({ not_interested_reason: v })} placeholder="Select reason (optional)" options={NOT_INTERESTED} />
                </Field>}
              </div>
            </Section>}
            <Section n={connected ? 4 : 2} title="Next Action" testid="lo-section-next">
              <div style={C.grid}>
                <Field label="Next Action" req error={errors.next_action} hint="Choose the action that should happen after this call." full>
                  <Select testid="lo-next-action" value={d.next_action} onChange={(v) => set({ next_action: v })} placeholder="Select next action" options={DISPO.nextActions} error={errors.next_action} />
                </Field>
                {DATED.includes(d.next_action) && (<>
                  <Field label="Follow-up Date" req error={errors.followup_date} hint={d.next_action !== "Book Product Demo" ? "A follow-up task is added to this client's Tasks tab for you." : undefined}>
                    <input type="date" data-testid="lo-followup-date" value={d.followup_date} onChange={(e) => set({ followup_date: e.target.value })} style={{ ...C.input, borderColor: errors.followup_date ? "#f97066" : "#cfd6e2" }} />
                  </Field>
                  <Field label="Follow-up Time (optional)">
                    <input type="time" data-testid="lo-followup-time" value={d.followup_time} onChange={(e) => set({ followup_time: e.target.value })} style={C.input} />
                  </Field>
                </>)}
                {d.next_action === "Send Product Information" && <Field label="Products to Send Information For" req error={errors.info_products} full>
                  <ProductTiles prefix="lo-info" selected={d.info_products} onToggle={(p) => set({ info_products: toggle(d.info_products, p) })} />
                </Field>}
                {d.next_action === "Start Free Trial" && <Field label="Product for Free Trial" req error={errors.trial_product}>
                  <Select testid="lo-trial-product" value={d.trial_product} onChange={(v) => set({ trial_product: v })} placeholder="Select trial product" options={DISPO.products} error={errors.trial_product} />
                </Field>}
                {d.next_action === "Book Product Demo" && <Field label="Product(s) for Demo" hint="Filled in from the products pitched." full>
                  <input data-testid="lo-demo-products" readOnly disabled value={d.products_pitched.join(", ") || "—"} style={{ ...C.input, background: "#f2f4f7", color: "#475467" }} />
                </Field>}
                {d.next_action === "Book Product Demo" && (
                  <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <button type="button" data-testid="lo-setup-demo" onClick={() => setDemoOpen(true)}
                      style={{ border: "1px solid #528bff", background: "#f5f8ff", color: "#175cd3", borderRadius: 9, padding: "9px 14px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <CalendarPlus style={{ width: 14, height: 14 }} /> {booked ? "Book another demo" : "Set up demo"}
                    </button>
                    {booked
                      ? <span data-testid="lo-demo-booked" style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#127152", fontSize: 12.5, fontWeight: 600 }}><CheckCircle2 style={{ width: 14, height: 14 }} /> Demo booked · {booked.date} {booked.time}</span>
                      : <span style={C.hint}>Opens the client demo booking. Save the outcome after booking.</span>}
                  </div>
                )}
              </div>
            </Section>
            <Section n={connected ? 5 : 3} title="Call Notes" testid="lo-section-notes" last>
              <Field label="Notes (optional)" hint="Saved notes are also added to the top of the Next Action & Ownership notes.">
                <textarea data-testid="lo-notes" value={d.notes} onChange={(e) => set({ notes: e.target.value })} style={{ ...C.input, minHeight: 86, resize: "vertical" }} />
              </Field>
            </Section>
          </div>
        </div>
        <aside data-testid="lo-history-panel" style={{ overflowY: "auto", minHeight: 0 }}>
          <EhiOutcomeHistory clientId={clientId} />
        </aside>
        </div>
        <footer style={{ padding: "16px 22px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span data-testid="lo-error-summary" style={{ color: "#d92d20", fontSize: 12, fontWeight: 600 }}>{errCount ? `Please complete ${errCount} required field${errCount === 1 ? "" : "s"}.` : ""}</span>
          <div style={{ display: "flex", gap: 10 }}>
            <button data-testid="lo-clear" type="button" onClick={() => { setD(EMPTY); setErrors({}); }} style={{ border: "1px solid #cfd6e2", background: "#f2f4f7", color: "#344054", borderRadius: 9, padding: "10px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Clear Form</button>
            <button data-testid="lo-save" type="button" disabled={saving} onClick={submit} style={{ border: "none", background: "#175cd3", color: "#fff", borderRadius: 9, padding: "10px 18px", fontWeight: 700, fontSize: 13, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1, fontFamily: "inherit" }}>{saving ? "Saving…" : "Save Outcome"}</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
